import React, { useState, useCallback } from 'react';
import './RatingButton.css';

export type Rating = 'hard' | 'medium' | 'easy' | 'skip';

export interface RatingButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick'
> {
  /** Rating type */
  rating: Rating;
  /** Click handler with rating */
  onClick?: (rating: Rating) => void;
  /** Show label text */
  showLabel?: boolean;
  /** Show subtitle (first 3 sessions) */
  showSubtitle?: boolean;
  /** Additional CSS class */
  className?: string;
}

const RATING_CONFIG: Record<Rating, { label: string; subtitle: string }> = {
  hard: { label: 'Hard', subtitle: 'Show again soon' },
  medium: { label: 'Medium', subtitle: 'Getting there' },
  easy: { label: 'Easy', subtitle: 'I know this' },
  skip: { label: 'Skip', subtitle: '' },
};

/**
 * Rating button for flashcard difficulty feedback.
 * Follows DES-033 design system - all ratings feel equally valid.
 * Warm coral for Hard (supportive, not punishing).
 */
export const RatingButton = React.forwardRef<HTMLButtonElement, RatingButtonProps>(
  (
    { rating, onClick, showLabel = true, showSubtitle = false, className = '', disabled, ...props },
    ref
  ) => {
    const [isConfirmed, setIsConfirmed] = useState(false);
    const config = RATING_CONFIG[rating];

    const handleClick = useCallback(() => {
      setIsConfirmed(true);
      onClick?.(rating);
      // Reset after animation completes
      setTimeout(() => setIsConfirmed(false), 300);
    }, [onClick, rating]);

    const classes = [
      'stc-rating-btn',
      `stc-rating-btn--${rating}`,
      isConfirmed && 'confirmed',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type="button"
        className={classes}
        onClick={handleClick}
        disabled={disabled}
        aria-label={`Rate as ${config.label}${config.subtitle ? `: ${config.subtitle}` : ''}`}
        {...props}
      >
        {showLabel && <span className="stc-rating-btn__label">{config.label}</span>}
        {showSubtitle && config.subtitle && (
          <span className="stc-rating-btn__subtitle">{config.subtitle}</span>
        )}
      </button>
    );
  }
);

RatingButton.displayName = 'RatingButton';
